import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { FullScreenLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";

// Return-to after re-login: the page the user was on (path + query, e.g. a
// till phone's `/waste-meals?section=co`) travels in router state and
// LoginPage navigates back to it once the session is restored.
function LoginRedirect() {
    const location = useLocation();
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
}

export default function ProtectedRoute() {
    const { me, loading } = useAuth();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    return <Outlet />;
}

export function AdminRoute({ children }) {
    const { me, loading, isSuperAdmin } = useAuth();
    const { t } = useI18n();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    if (!isSuperAdmin) {
        return (
            <div
                data-testid={OPS.error.e403}
                className="flex flex-col items-center justify-center gap-3 py-24 text-center"
            >
                <ShieldAlert className="h-12 w-12 text-[color:var(--danger)]" />
                <h2 className="text-xl font-bold text-[color:var(--ink)]">{t("forbidden_title")}</h2>
            </div>
        );
    }
    return children;
}

// Users tab gate — accepts super_admin (unlimited) OR quality_manager
// (RESTRICTED — sees only inspector / branch_manager / qc_supervisor). The
// backend enforces the scope; this is just the client-side entry gate.
export function UserAdminRoute({ children }) {
    const { me, loading, isUserAdmin } = useAuth();
    const { t } = useI18n();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    if (!isUserAdmin) {
        return (
            <div
                data-testid={OPS.error.e403}
                className="flex flex-col items-center justify-center gap-3 py-24 text-center"
            >
                <ShieldAlert className="h-12 w-12 text-[color:var(--danger)]" />
                <h2 className="text-xl font-bold text-[color:var(--ink)]">{t("forbidden_title")}</h2>
            </div>
        );
    }
    return children;
}

// Infrastructure gate — Ingest Status + Backups. super_admin OR it_admin.
export function SystemAdminRoute({ children }) {
    const { me, loading, isSystemAdmin } = useAuth();
    const { t } = useI18n();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    if (!isSystemAdmin) {
        return (
            <div
                data-testid={OPS.error.e403}
                className="flex flex-col items-center justify-center gap-3 py-24 text-center"
            >
                <ShieldAlert className="h-12 w-12 text-[color:var(--danger)]" />
                <h2 className="text-xl font-bold text-[color:var(--ink)]">{t("forbidden_title")}</h2>
            </div>
        );
    }
    return children;
}

// Operations dashboard gate — the all-branch live grid. super_admin OR the ops_dashboard capability.
export function OpsDashboardRoute({ children }) {
    const { me, loading, canOpsDashboard } = useAuth();
    const { t } = useI18n();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    if (!canOpsDashboard) {
        return (
            <div
                data-testid={OPS.error.e403}
                className="flex flex-col items-center justify-center gap-3 py-24 text-center"
            >
                <ShieldAlert className="h-12 w-12 text-[color:var(--danger)]" />
                <h2 className="text-xl font-bold text-[color:var(--ink)]">{t("forbidden_title")}</h2>
            </div>
        );
    }
    return children;
}

// Branch Check-in gate — dedicated open/close page. super_admin OR the
// branch_checkin capability (any branch) OR branch_manager (own branch, enforced server-side).
export function CheckinRoute({ children }) {
    const { me, loading, canBranchCheckin, isBranchManager } = useAuth();
    const { t } = useI18n();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    if (!(canBranchCheckin || isBranchManager)) {
        return (
            <div
                data-testid={OPS.error.e403}
                className="flex flex-col items-center justify-center gap-3 py-24 text-center"
            >
                <ShieldAlert className="h-12 w-12 text-[color:var(--danger)]" />
                <h2 className="text-xl font-bold text-[color:var(--ink)]">{t("forbidden_title")}</h2>
            </div>
        );
    }
    return children;
}

// Branch KPI gate — super_admin OR the branch_kpi capability. Branch managers
// no longer get automatic access: they need the branch_kpi toggle granted
// (then they are scoped to their own branch, enforced server-side).
export function KpiRoute({ children }) {
    const { me, loading, canBranchKpi } = useAuth();
    const { t } = useI18n();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    if (!canBranchKpi) {
        return (
            <div
                data-testid={OPS.error.e403}
                className="flex flex-col items-center justify-center gap-3 py-24 text-center"
            >
                <ShieldAlert className="h-12 w-12 text-[color:var(--danger)]" />
                <h2 className="text-xl font-bold text-[color:var(--ink)]">{t("forbidden_title")}</h2>
            </div>
        );
    }
    return children;
}

// CCTV hub gate — CCTV Monitoring module OR Branch Check-in authority
// (the "CCTV employee"). The hub renders whichever sub-tabs are permitted.
export function CctvHubRoute({ children }) {
    const { me, loading, canBranchCheckin, canCctvManage } = useAuth();
    const { t } = useI18n();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    const cctvOk = me?.nav?.find((tb) => tb.key === "cctv_monitoring")?.permitted;
    if (!cctvOk && !canBranchCheckin && !canCctvManage) {
        return (
            <div
                data-testid={OPS.error.e403}
                className="flex flex-col items-center justify-center gap-3 py-24 text-center"
            >
                <ShieldAlert className="h-12 w-12 text-[color:var(--danger)]" />
                <h2 className="text-xl font-bold text-[color:var(--ink)]">{t("forbidden_title")}</h2>
            </div>
        );
    }
    return children;
}

// Gate a route behind a specific permission tab (e.g. "financials"). An
// optional `fallbackTabKey` lets a caller pass through if EITHER the primary
// OR the fallback tab is granted — used by the merged Quality hub which
// admits either `qc_inspection` OR `qc_waste` users.
export function TabRoute({ tabKey, fallbackTabKey, allowExtra = false, children }) {
    const { me, loading } = useAuth();
    const { t } = useI18n();
    if (loading) return <FullScreenLoader />;
    if (!me) return <LoginRedirect />;
    const primaryOk = me?.nav?.find((tb) => tb.key === tabKey)?.permitted;
    const fallbackOk = fallbackTabKey
        ? me?.nav?.find((tb) => tb.key === fallbackTabKey)?.permitted
        : false;
    if (!primaryOk && !fallbackOk && !allowExtra) {
        return (
            <div
                data-testid={OPS.error.e403}
                className="flex flex-col items-center justify-center gap-3 py-24 text-center"
            >
                <ShieldAlert className="h-12 w-12 text-[color:var(--danger)]" />
                <h2 className="text-xl font-bold text-[color:var(--ink)]">{t("forbidden_title")}</h2>
            </div>
        );
    }
    return children;
}
